import { useApi } from '@mainstage/core-plugin-api';
import { useMemo } from 'react';
import {
  CollatorType,
  generateMarketplaceProjectsFilters,
  generateProvisioningConsumablesFilters,
  MarketplaceProjectConfigurationType,
  ProvisioningConsumableConfigurationType,
} from '@agilelab/plugin-wb-search-common';
import { useQuery as useReactQuery } from '@tanstack/react-query';
import {
  ConfigurationApi,
  configurationApiRef,
} from '@agilelab/plugin-wb-configuration-common';

const STALE_TIME = 1000 * 60 * 5;

export const MARKETPLACE_PROJECTS_QUERY_KEY = [
  'marketplaceProjectsFiltersConfiguration',
];

export const PROVISIONING_CONSUMABLES_QUERY_KEY = [
  'provisioningConsumablesFiltersConfiguration',
];

type ConfigMap = {
  [CollatorType.MARKETPLACE_PROJECTS]: MarketplaceProjectConfigurationType;
  [CollatorType.PROVISIONING_CONSUMABLES]: ProvisioningConsumableConfigurationType;
};

type ConfigMapEntry<T> = {
  queryKey: string[];
  queryFn: (wbConfigApi: ConfigurationApi) => Promise<T | undefined>;
};

const configMap: {
  [K in CollatorType]: ConfigMapEntry<ConfigMap[K]>;
} = {
  [CollatorType.MARKETPLACE_PROJECTS]: {
    queryKey: MARKETPLACE_PROJECTS_QUERY_KEY,
    queryFn: wbConfigApi =>
      wbConfigApi.get<MarketplaceProjectConfigurationType>({
        namespace: 'marketplace',
        key: 'search.marketplace-projects.filters',
      }),
  },
  [CollatorType.PROVISIONING_CONSUMABLES]: {
    queryKey: PROVISIONING_CONSUMABLES_QUERY_KEY,
    queryFn: wbConfigApi =>
      wbConfigApi.get<ProvisioningConsumableConfigurationType>({
        namespace: 'platform',
        key: 'search.provisioning-consumables.filters',
      }),
  },
};

export const useFiltersByConfiguration = <T extends CollatorType>(
  type: T,
  noCache?: boolean,
) => {
  const wbConfigApi = useApi(configurationApiRef);

  const config = configMap[type];

  const { queryKey, queryFn } = config;

  const { data, isLoading, error } = useReactQuery<ConfigMap[T] | undefined>({
    queryKey,
    queryFn: () => queryFn(wbConfigApi),
    staleTime: noCache ? 0 : STALE_TIME,
  });

  return { data, isLoading, error };
};

export const useSearchFilterConfig = (
  type: CollatorType,
  taxonomyRef?: string,
) => {
  const { data: filters, isLoading } = useFiltersByConfiguration(type);

  const filtersConfig = useMemo(() => {
    if (isLoading && !filters) return [];
    switch (type) {
      case CollatorType.MARKETPLACE_PROJECTS:
        return generateMarketplaceProjectsFilters(filters, true, taxonomyRef);

      case CollatorType.PROVISIONING_CONSUMABLES:
        return generateProvisioningConsumablesFilters(filters, true);

      default:
        return [];
    }
  }, [filters, type, taxonomyRef, isLoading]);

  const filtersConfigReady = !isLoading && !!filters;
  return { filtersConfig, filtersConfigReady };
};
